import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { useParams } from 'react-router'
import { AlertTriangle } from 'lucide-react'
import { ErrorState, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useLoanDetail, useLoanMutations } from '../api'
import {
  INSTALMENT_STATUS_LABELS, INTEREST_METHOD_LABELS, LOAN_STATUS_LABELS,
} from '../types'
import type { InstalmentStatus, InterestMethod, LoanStatus } from '../types'
import { DatePicker } from '@/components/ui/date-picker'

/** One loan: the schedule that was written at approval, and what has been taken against it. */
export function LoanDetailPage() {
  useBreadcrumb()
  const { id = '' } = useParams()
  const detail = useLoanDetail(id)
  const { submit, approve, reject, disburse, settle } = useLoanMutations()

  const [settling, setSettling] = useState<{ amount: string; settledOn: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (detail.isError) return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
  if (detail.isPending || !detail.data) return <Skeleton className="h-96 w-full" />

  const { loan, schedule, repayments, scheduleOutstanding } = detail.data
  const status = loan.status as LoanStatus
  const drifted = Math.abs(scheduleOutstanding - loan.outstandingPrincipal) > 0.001

  async function act(run: () => Promise<unknown>, fallback: string) {
    setError(null)
    try {
      await run()
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? fallback)
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={loan.reference}
        subtitle={`${loan.employeeName ?? ''} · ${loan.principal} ${loan.currency} over ${loan.tenorMonths} months`}
        actions={
          <span className="flex flex-wrap gap-2">
            {status === 'DRAFT' && (
              <Button type="button" onClick={() => void act(
                () => submit.mutateAsync(id), 'It could not be submitted')}>
                Submit
              </Button>
            )}
            {status === 'SUBMITTED' && (
              <>
                <Button type="button" onClick={() => void act(
                  () => approve.mutateAsync({ id }), 'It could not be approved')}>
                  Approve
                </Button>
                <Button type="button" variant="outline" onClick={() => void act(
                  () => reject.mutateAsync({ id }), 'It could not be rejected')}>
                  Reject
                </Button>
              </>
            )}
            {status === 'APPROVED' && (
              <Button type="button" onClick={() => void act(
                () => disburse.mutateAsync({
                  id, disbursedOn: todayLocal(),
                }), 'It could not be disbursed')}>
                Record the money out
              </Button>
            )}
            {status === 'DISBURSED' && (
              <Button
                type="button" variant="outline"
                onClick={() => {
                  setError(null)
                  setSettling({
                    amount: String(loan.outstandingPrincipal),
                    settledOn: todayLocal(),
                  })
                }}
              >
                Settle early
              </Button>
            )}
          </span>
        }
      />

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="p-4">
          <p className="text-sm text-slate-600">Status</p>
          <StatusBadge status={loan.status} label={LOAN_STATUS_LABELS[status] ?? loan.status} />
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-600">Still owed</p>
          <p className="text-lg font-semibold">{loan.outstandingPrincipal}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-600">Interest</p>
          <p className="text-sm">
            {INTEREST_METHOD_LABELS[loan.interestMethod as InterestMethod]}
            {loan.interestRate != null && ` at ${loan.interestRate}%`}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-600">Disbursed</p>
          <p className="text-lg font-semibold">{loan.disbursedOn ?? '—'}</p>
        </Card>
      </div>

      {drifted && (
        <p role="alert" className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          The balance on the loan ({loan.outstandingPrincipal}) does not match the schedule
          ({scheduleOutstanding}). The schedule is the record; the balance is a cache, and the
          two disagreeing means something wrote one without the other.
        </p>
      )}

      <Card>
        <h2 className="border-b border-slate-200 p-3 text-base font-semibold">Schedule</h2>
        {schedule.length === 0 ? (
          <p className="p-4 text-sm text-slate-600">
            The schedule is written when the loan is approved.
          </p>
        ) : (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-2 text-start font-medium">#</th>
                  <th className="p-2 text-start font-medium">Due</th>
                  <th className="p-2 text-end font-medium">Principal</th>
                  <th className="p-2 text-end font-medium">Interest</th>
                  <th className="p-2 text-end font-medium">Total</th>
                  <th className="p-2 text-end font-medium">Paid</th>
                  <th className="p-2 text-end font-medium">Arrears</th>
                  <th className="p-2 text-start font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((instalment) => (
                  <tr key={instalment.id} className="border-b border-slate-100">
                    <td className="p-2">{instalment.seq}</td>
                    <td className="p-2">{instalment.dueOn}</td>
                    <td className="p-2 text-end">{instalment.principalDue}</td>
                    <td className="p-2 text-end">{instalment.interestDue}</td>
                    <td className="p-2 text-end">{instalment.totalDue}</td>
                    <td className="p-2 text-end text-slate-600">
                      {instalment.principalPaid + instalment.interestPaid}
                    </td>
                    <td className={`p-2 text-end ${instalment.arrears > 0 ? 'text-amber-700' : ''}`}>
                      {instalment.arrears > 0 ? instalment.arrears : '—'}
                    </td>
                    <td className="p-2">
                      <StatusBadge
                        status={instalment.status}
                        label={INSTALMENT_STATUS_LABELS[instalment.status as InstalmentStatus]
                          ?? instalment.status}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="border-t border-slate-200 p-3 text-xs text-slate-600">
          Written once, at approval, and never recomputed. A schedule that is recalculated
          changes when the loan is edited or the arithmetic is improved, and then disagrees
          with what was actually deducted.
        </p>
      </Card>

      <Card>
        <h2 className="border-b border-slate-200 p-3 text-base font-semibold">What has been taken</h2>
        {repayments.length === 0 ? (
          <p className="p-4 text-sm text-slate-600">Nothing yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-slate-600">
              <tr>
                <th className="p-2 text-start font-medium">When</th>
                <th className="p-2 text-end font-medium">Principal</th>
                <th className="p-2 text-end font-medium">Interest</th>
                <th className="p-2 text-start font-medium">From</th>
              </tr>
            </thead>
            <tbody>
              {repayments.map((repayment) => (
                <tr key={repayment.id} className="border-b border-slate-100">
                  <td className="p-2">{repayment.paidOn}</td>
                  <td className="p-2 text-end">{repayment.principalAmount}</td>
                  <td className="p-2 text-end">{repayment.interestAmount}</td>
                  <td className="p-2 text-slate-600">{repayment.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <FormDialog
        open={settling != null}
        onOpenChange={(open) => { if (!open) setSettling(null) }}
        title="Settle this loan now"
        description="Any rebate of unearned interest is your policy, so the figure is entered rather than derived."
        submitLabel="Settle it"
        loading={settle.isPending}
        error={error}
        onSubmit={() => {
          if (!settling) return
          void act(async () => {
            await settle.mutateAsync({
              id, amount: Number(settling.amount), settledOn: settling.settledOn,
            })
            setSettling(null)
          }, 'It could not be settled')
        }}
      >
        {settling && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="settle-amount">Amount taken</Label>
              <Input
                id="settle-amount" type="number" value={settling.amount}
                onChange={(event) => setSettling({ ...settling, amount: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Pre-filled with the outstanding balance. Change it if a rebate applies — what is
                recorded is the figure entered, and that it was entered.
              </p>
            </div>
            <div>
              <Label htmlFor="settle-on">On</Label>
              <DatePicker
                id="settle-on" value={settling.settledOn}
                onChange={(event) => setSettling({ ...settling, settledOn: event.target.value })}
              />
            </div>
            <p className="text-sm text-slate-600">
              Whatever the settlement does not cover is closed rather than left showing as due —
              an early settlement is an agreement, and the remaining instalments are part of it.
            </p>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
